import styled from 'styled-components';

import { useFooterContactUsTag, useFooterTextUsTag } from '../../../tag-manager/footer';
import { useHomePageTag } from '../../../tag-manager/homepage';
import { useSocialMediaTag } from '../../../tag-manager/social-medial';
import { aboutPage, companyEmail, companyPhone, faqPage, howItWorksPage, guestAgreement, hostAgreement, privacyPolicy, termsOfUse } from '../../helpers';
import { colors, LogoFacebookIcon, LogoInstagramIcon, LogoLinkedinIcon, LogoTiktokIcon, mobile, tablet } from '../../styles';

// AEO-7c: <footer> so every page exposes a contentinfo landmark. It is a
// sibling of <main>, never a child, which is what makes it a real landmark.
// PageWrapper styles this element through `& > div:last-child`, which a
// <footer> no longer matches, so `& > footer` was added there in the same
// commit. Without it this bar loses its shadow and its top border.
const Background = styled.footer`
  width: 100%;
  position: fixed;
  bottom: 0;
  background-color: white;

  @media (max-width: ${mobile}) {
    position: relative;
  }
`;

const Wrapper = styled.div`
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 0 44px;
  font-size: 14px;
  color: ${colors.defaultText};

  a {
    color: ${colors.defaultText};

    @media (max-width: ${mobile}) {
      font-weight: 500;
    }
  }

  @media (max-width: ${tablet}) {
    padding: 0 24px;
  }

  @media (max-width: ${mobile}) {
    height: auto;
    flex-direction: column;
    padding: 15px;
    font-size: 12px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  // AEO-9: six links do not fit one phone-width line. Without this they
  // run off the side of the screen instead of moving to a second row.
  flex-wrap: wrap;
  row-gap: 4px;

  @media (max-width: ${mobile}) {
    width: 100%;
    justify-content: flex-start;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const MobileHide = styled.span`
  @media (max-width: ${mobile}) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;
  font-weight: 300px;
  width: 100%;

  @media (max-width: ${mobile}) {
    display: block;
    margin: 10px 0 22px;
  }
`;

const SocialWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;

  @media (max-width: ${mobile}) {
    gap: 40px;
    margin-bottom: 12px;
  }

  svg {
    display: inline-block;
    height: 23px;
    width: 23px;
    fill: ${colors.secondaryButton};
  }

  a:hover {
    opacity: 0.9;
  }
`;

const Footer = () => {
  return (
    <Background>
      <Wrapper>
        <TextWrapper>
          <MobileHide>© {new Date().getFullYear()} Yedding, LLC</MobileHide>
          <MobileHide>/</MobileHide>
          {/* AEO-11a: who runs Yedding. Same tab, like the FAQ below it. */}
          <a href={aboutPage} onClick={() => useHomePageTag({ label: 'About' })}>
            About
          </a>
          <span>/</span>
          {/* AEO-11b: the booking steps. Same tab, like About and FAQ. */}
          <a href={howItWorksPage} onClick={() => useHomePageTag({ label: 'How it works' })}>
            How it works
          </a>
          <span>/</span>
          {/* AEO-10: the FAQ. Same tab on purpose: it is an in-app route,
              so it needs no reload and no new window. */}
          <a href={faqPage} onClick={() => useHomePageTag({ label: 'FAQ' })}>
            FAQ
          </a>
          <span>/</span>
          <a href={privacyPolicy} target="_blank" onClick={() => useHomePageTag({ label: 'Privacy Policy' })}>
            Privacy
          </a>
          <span>/</span>
          {/* <a href={deleteAccount} target="_blank">
            Delete Account
          </a>
          <span>/</span> */}
          <a href={termsOfUse} target="_blank" onClick={() => useHomePageTag({ label: 'Terms of Use' })}>
            Terms & Conditions
          </a>
          <span>/</span>
          {/* AEO-9: all four legal documents are linked from here, so a
              reader and a crawler can reach every one of them from any page. */}
          <a href={guestAgreement} target="_blank" onClick={() => useHomePageTag({ label: 'Guest Terms' })}>
            Guest Terms
          </a>
          <span>/</span>
          <a href={hostAgreement} target="_blank" onClick={() => useHomePageTag({ label: 'Host Terms' })}>
            Host Terms
          </a>
          <span>/</span>
          <a href={`mailto:${companyEmail}`} target="_blank" onClick={useFooterContactUsTag}>
            Contact Us
          </a>
          <span>/</span>
          <a href={`sms:${companyPhone}`} target="_blank" onClick={useFooterTextUsTag}>
            Text Us
          </a>
        </TextWrapper>
        <MobileOnly>© {new Date().getFullYear()} Yedding, LLC</MobileOnly>

        <SocialWrapper>
          <a target="_blank" href=" https://www.facebook.com/Yeddingllc" onClick={() => useSocialMediaTag({ label: 'Facebook' })}>
            <LogoFacebookIcon />
          </a>
          <a target="_blank" href="https://www.instagram.com/yeddingllc/" onClick={() => useSocialMediaTag({ label: 'Instagram' })}>
            <LogoInstagramIcon />
          </a>
          <a target="_blank" href="https://www.tiktok.com/@yeddingllc" onClick={() => useSocialMediaTag({ label: 'TikTok' })}>
            <LogoTiktokIcon />
          </a>
          <a target="_blank" href="https://www.linkedin.com/company/yeddingllc/" onClick={() => useSocialMediaTag({ label: 'LinkedIn' })}>
            <LogoLinkedinIcon />
          </a>
        </SocialWrapper>
      </Wrapper>
    </Background>
  );
};

export default Footer;
